import React, { useState, useEffect } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { BreadcrumbItem, Breadcrumb } from 'react-bootstrap';

// -----------------------------
// EventCalendar (final merged)
// - Uses react-big-calendar (named export Calendar)
// - Sets up moment localizer correctly for React 18 compatible versions
// - Embeds the "Today Events" sidebar directly (no separate TodayEvents import)
// - Keeps the "Click me" link in each date header to filter events for that date
// - Month-only view preserved
// -----------------------------

// Set locale for moment
moment.locale('en-GB');

// Create localizer using momentLocalizer (required by react-big-calendar)
const localizer = momentLocalizer(moment);

function EventCalendar(props) {
  // State to hold fetched calendar events (filtered to Library dept)
  const [events, setEvents] = useState([]);

  // State for list of events filtered by clicking a date ("Click me")
  const [items, setItems] = useState([]);

  // Fetch events from API and prepare fields expected by react-big-calendar
useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await fetch(
        "https://habib.edu.pk/events-calendar/wp-json/v1.0.1/DisplayEvent"
      );
      const data = await res.json();

      // Keep only Library department
      let calanderData = data.filter(item => item.Department === "Library");

      // Normalize dates
      calanderData = calanderData.map(o => {
        const start = new Date(o.start);
        const duration = parseInt(o.Duration || 0, 10);
        const end = addMinutes(new Date(start), duration);

        return {
          ...o,
          start,
          end,
          allDay: true,
          Date: start.toLocaleDateString(),
          Time: start.toLocaleTimeString(),
          EndTime: end.toLocaleTimeString(),
        };
      });

      // 🔵 Filter events for CURRENT month only
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      calanderData = calanderData.filter(ev =>
        ev.start.getFullYear() === currentYear &&
        ev.start.getMonth() === currentMonth
      );

      setEvents(calanderData);

    } catch (err) {
      console.error("Failed to fetch events", err);
    }
  };

  fetchData();
}, []);

  // Utility to add minutes to a Date object (returns mutated date)
  function addMinutes(date, minutes) {
    date.setMinutes(date.getMinutes() + minutes);
    return date;
  }

  // Custom date header renderer for the month view
  // Shows the date number and a "Click me" link that filters events for that date
  function CustomDateHeader({ date, label }) {
    return (
      <div>
        <p className="date-number">{label}</p>
        <Link className="ClickDay" to="" onClick={(e) => { e.preventDefault(); filterSameDate(date); }}>Click me</Link>
      </div>
    );
  }

  // Filter events for a specific date and store them in `items` for the sidebar
  const filterSameDate = (param) => {
    if (!param) return setItems([]);
    const filtered = events.filter(ev => ev.start.toLocaleDateString() === param.toLocaleDateString());
    setItems(filtered);
  };

  // Event style getter for coloring events (uses hexColor property on event objects)
  const eventStyleGetter = (event) => ({
    style: {
      backgroundColor: event.hexColor ? ('#' + event.hexColor) : '#3174ad',
      borderRadius: '0px',
      opacity: 0.9,
      color: '#fff',
      border: 0,
      display: 'block',
    }
  });

  // Build list of "today" events (used in the right column)
  const currentDate = new Date();
  const FilterEvents = events.map(o => ({ event: o })).filter(item => {
    // original code pushed everything; keep same logic but we will show only those matching today
    // We'll treat all events as eligible but display "No Event Today" when none match today's date
    return true;
  });

  return (
    <div className="EventCalendarMainWrap">
      <div className="research-box-area">
        <Container>
          <div className="information-content">
            <h2>Event Calendar</h2>
          </div>
        </Container>
      </div>

      <div className="calenderBgWrap">
        <Container>
          <div className="eventCalendars">
            <div className="main-wrapper">
              <div className="main-calendar">
                {/* Calendar: month-only view, localizer passed in */}
                <BigCalendar
                  localizer={localizer}
                  events={events}
                  defaultDate={new Date()}
                  views={{ month: true }}
                  eventPropGetter={eventStyleGetter}
                  components={{ month: { dateHeader: CustomDateHeader } }}
                />
              </div>

              {/* Sidebar: shows filtered items (from clicking a date) */}
              <div className="modal-show">
                <div className="shopping-list">
                  {items && items.length > 0 && <h2 className="modal-title">{moment(items[0].Date).format('ll')}</h2>}
                  {items.map(postEvent => (
                    <div key={postEvent.event_id}>
                      <a href={`https://habib.edu.pk/events-calendar/Event/${postEvent.title}`} className={"list-item-" + postEvent.event_id}>
                        <div className="event-list-day-box">
                          <img src={postEvent.image} alt="" />
                          <div className="list-day-box-content">
                            <h4 className="event-title">{postEvent.title}</h4>
                            <p className="even-location"><strong>Location:</strong> {postEvent.Location}</p>
                            <p className="event-time">{postEvent.Time}</p>
                            <p className="event-date">{postEvent.Date}</p>
                            <p className="event-date">{postEvent.EndTime}</p>
                            <div className="event-description" dangerouslySetInnerHTML={{ __html: postEvent.event_description }} />
                          </div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right column: Today's events (keeps original behaviour) */}
            <div className="TodayEventsItems">
              <div className="today-events">
                <ul>
                  {FilterEvents && FilterEvents.length ? (
                    <>
                      <h5>Today's Events</h5>
                      {FilterEvents.map(postEvent => (
                        <li key={postEvent.event.event_id} className={"list-item-" + (postEvent.event.event_id)}>
                          <a href={`https://habib.edu.pk/events-calendar/Event/${postEvent.event.title}`} target="_blank" rel="noreferrer">
                            <img src="./assets/img/event-calendar-icon.svg" alt="" />
                            <div className="event-content">
                              <p className="event-date text-light">{postEvent.event.Time} - {postEvent.event.EndTime}</p>
                              <p className="event-date text-light">{postEvent.event.Date}</p>
                              <h4>{postEvent.event.title}</h4>
                              <p className="event-location text-light">Location:</p>
                              <p>{postEvent.event.Location}</p>
                            </div>
                          </a>
                        </li>
                      ))}
                    </>
                  ) : (
                    <div className="no-event-wrap">
                      <img src="./assets/img/no-event-icon.svg" alt="" />
                      <h5>No Event Today</h5>
                      <p className="no-event">
                        We will be organizing exciting events very soon. Please check our Facebook page to stay connected
                      </p>
                    </div>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}

export default EventCalendar;
